/**
 * Campanhas — operações de mídia (spec 02 §7 · passo 4.3b).
 *
 * Três ausências, e cada uma é uma decisão do modelo:
 *
 * - **Não existe entrega** (impressões, cliques, VTR). DS-06/D-72: `fimReal` é o
 *   único insumo de veiculação, e guardar entrega seria construir um ad server.
 * - **Margem não é estado.** A projeção é cálculo PURO (spec 02 §7.3); o contrato
 *   transporta a resposta, mas nenhuma coluna guarda o resultado calculado.
 * - **Não existe `codigo` de entrada.** `PV-0246·C1` é composto pelo gatilho a partir
 *   do código do pedido — a tela nunca o inventa.
 */
import { z } from 'zod'
import { MOTIVOS_SEM_VINCULO_DV360, STATUS_GRAVADOS_DA_CAMPANHA } from '@dv/domain'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'
import { dinheiroNaoNegativo } from './contas-pagar.js'
import { referenciaDeUsuario } from './usuarios.js'

/**
 * O status no vocabulário da tela da versão ANTERIOR (DS-657): os quatro valores que ela conhece. A Pausada viaja aqui
 * como 'Em veiculação', e só aqui (como a coluna antiga do portal, P-11); o valor gravado vai em `statusGravado`.
 */
export const statusDaCampanha = z.enum(['Programada', 'Em veiculação', 'Encerrada', 'Cancelada'])
export type StatusDaCampanha = z.infer<typeof statusDaCampanha>

/**
 * O status GRAVADO da campanha (D-189, P-01): 'Programada', 'Em veiculação', 'Pausada', 'Encerrada' ou 'Cancelada'. A
 * tela mostra o rótulo do mapa único (`rotuloDoStatusDaCampanha`: Em veiculação = "Em andamento", Encerrada =
 * "Concluída"), e o valor gravado nunca vai para o texto.
 */
export const statusGravadoDaCampanha = z.enum(STATUS_GRAVADOS_DA_CAMPANHA)
export type StatusGravadoDaCampanha = z.infer<typeof statusGravadoDaCampanha>

/** O mapa único de rótulos (P-01), o mesmo do domínio e de `fn_rotulo_status_campanha`, e o aviso de mês do Iniciar. */
export {
  inicioMudaDeMes,
  NOME_DO_ATO,
  ROTULO_DO_STATUS_DA_CAMPANHA,
  rotuloDoStatusDaCampanha,
  rotuloDoStatusNaFrase,
} from '@dv/domain'

/**
 * De onde veio o custo de mídia de um item. Desde a D-188 é sempre `'meta'` (o planejado): o campo fica por uma release,
 * com o valor neutro, para a aba aberta com a tela anterior (P-16, DS-651).
 */
export const origemDoCustoDeMidiaDoItem = z.enum(['apurado', 'meta'])
/** Da campanha inteira. Desde a D-188, sempre `'meta'` (P-16, DS-651). */
export const origemDoCustoDeMidiaDaCampanha = z.enum(['apurado', 'misto', 'meta'])
/**
 * O selo curto da margem da D-133. Desde a D-188 os selos "parcial" e "sem apuração" saíram (P-16): o campo fica por
 * uma release e vem sempre `null` (DS-651).
 */
export const seloDaMargem = z.enum(['parcial', 'meta']).nullable()
export type SeloDaMargem = z.infer<typeof seloDaMargem>

/** O escopo do gasto de um item (D-191, P-18): coberto pelas Campaigns DV360 vinculadas, ou externo a elas. */
export const escopoDoGasto = z.enum(['campaigns_vinculadas', 'externo'])
export type EscopoDoGasto = z.infer<typeof escopoDoGasto>
/** A fonte do gasto: o consolidado DV360 ou a apuração manual. O grupo coberto tem uma só (D-191, P-18). */
export const fonteDoGasto = z.enum(['dv360', 'manual'])
export type FonteDoGasto = z.infer<typeof fonteDoGasto>
/** "Acima do planejado" (P-15): algum excesso; ou, com o consolidado ainda parcial, já acima do planejado do grupo. */
export const acimaDoPlanejado = z.enum(['acima', 'acima_dados_parciais'])
export type AcimaDoPlanejado = z.infer<typeof acimaDoPlanejado>

/**
 * Por que o gasto total não existe (§4.3, DS-652): o código, a frase que a tela e o CSV mostram, e as linhas que ela
 * nomeia. A ausência nunca vira zero: sem total, a tela diz "Gasto total: indisponível" com estes motivos.
 *
 * `linhas` são as linhas da campanha (`item_campanha.id`, D-193 (2)); `itens` é a mesma lista no item do Pedido de
 * cada linha, para a tela anterior, que casa por ele (sai na v1.9.1).
 */
export const motivoDoGastoIndisponivel = z.object({
  codigo: z.enum(['sem_itens', 'itens_sem_escopo', 'sem_apuracao', 'grupo_sem_vinculo', 'consolidado_parcial', 'moeda_divergente']),
  texto: z.string(),
  itens: z.array(uuid),
  linhas: z.array(uuid).optional(),
})
export type MotivoDoGastoIndisponivel = z.infer<typeof motivoDoGastoIndisponivel>

/**
 * O consolidado DV360 da campanha como a lista e a ficha o resumem (§5.7). A E4 entrega o caminho; ele só existe com
 * Campaign vinculada (a 0199, E7), e até lá vem `null`. `estado`: completo até `ate`; parcial (`cobertas` de
 * `vinculadas`); substituído por apuração manual desde `substituidoDesde`; ou não associado a itens. O valor é quantia.
 */
export const consolidadoDv360NoGasto = z.object({
  estado: z.enum(['completo', 'parcial', 'substituido', 'nao_associado']),
  valor: z.string().nullable(),
  ate: dataNominal.nullable(),
  cobertas: z.number().int().nonnegative(),
  vinculadas: z.number().int().nonnegative(),
  substituidoDesde: dataNominal.nullable(),
})
export type ConsolidadoDv360NoGasto = z.infer<typeof consolidadoDv360NoGasto>

/**
 * A janela em que um PEDIDO veiculou, somada das campanhas dele (coluna da 3.2).
 *
 * Mora aqui, e não no contrato de pedidos, porque quem sabe o que é veiculação é a mídia
 * — é o mesmo lugar em que a DS-06 já declara que `fimReal` é o único insumo dela.
 *
 * Vem PRONTA do servidor, como a Situação: a tela não soma datas nem compara duas para
 * decidir o que mostrar (spec 05 §4).
 */
export const janelaDeVeiculacao = z.object({
  /** A menor abertura entre as campanhas vivas do pedido. */
  inicio: dataNominal,
  /** O maior fim PLANEJADO. É o intervalo que a coluna mostra. */
  fim: dataNominal,
  /**
   * Quando a veiculação terminou de fato, e SÓ quando isso difere do plano.
   *
   * `null` é o caso comum: ninguém encerrou nada fora do previsto, e a janela é a
   * planejada. Preenchido, é a data em que o ato de encerrar veiculação fechou a
   * última campanha do pedido, e a coluna a mostra entre parênteses.
   *
   * O protótipo NÃO decide isto: o "(até 31/07)" que ele mostra no PV-0234 é a ponta
   * exata de um rótulo grosso ("jun–jul") num pedido que ainda está EM VEICULAÇÃO, e
   * campanha no ar não tem fim real (encerrar é ato, DS-06). A pergunta que este campo
   * responde é outra, e mais útil: a veiculação parou em dia diferente do plano?
   */
  encerradaEm: dataNominal.nullable(),
})
export type JanelaDeVeiculacao = z.infer<typeof janelaDeVeiculacao>

export const itemDeCampanha = z.object({
  /** A identidade da LINHA da campanha (D-190, D-193 (2)): o gasto, a fonte e o histórico casam por ela. */
  id: uuid,
  /**
   * O item do pedido que a linha SELECIONA: campanha não veicula o que não foi vendido. É a origem gravada
   * (`itemPedidoOrigemId`) enquanto o item existe no Pedido.
   */
  itemPedidoId: uuid,
  /** A origem comercial da linha, gravada na inclusão e imutável (0203). Opcional só para o dublê anterior. */
  itemPedidoOrigemId: uuid.optional(),
  /** Quando o Pedido excluiu o item de origem (P2-c, v1.9.1); `null` com a origem presente. */
  origemRemovidaEm: z.string().nullable().optional(),
  produtoId: uuid,
  produtoNome: z.string(),
  subprodutoId: uuid.nullable(),
  subprodutoNome: z.string().nullable(),
  /** `null` = a sessão pode operar a campanha, mas não pode ver valores financeiros. */
  valor: z.string().nullable(),
  /**
   * O custo de mídia da linha em PERCENTUAL sobre o valor dela — excludente com
   * `custoMidiaReais` (0096): a linha diz o custo de um jeito ou do outro.
   *
   * Os DOIS `null` significam "usa o default do produto" (DS-35). Copiar o default para
   * cá criaria uma segunda fonte que envelhece sozinha.
   */
  custoMidiaPct: z.string().nullable(),
  /**
   * O custo de mídia da linha em DINHEIRO, quando o fornecedor cobra valor fechado.
   *
   * É ABSOLUTO: mudar o valor da campanha reescala as linhas (D-63) e não mexe neste
   * custo, exatamente como `outrosReais`. E é QUANTIA: vem `null` para quem não tem a
   * flag financeira de custos, na mesma régua de `outrosReais`.
   */
  custoMidiaReais: z.string().nullable(),
  /**
   * Percentual SOBRE O VALOR do item (D-62), nunca sobre o custo de mídia — é o que faz
   * Σ das margens dos itens fechar com a margem da campanha (G15).
   *
   * `null` significa "usa o parâmetro do catálogo" (DS-35).
   */
  licenciamentoPct: z.string().nullable(),
  outrosReais: z.string().nullable(),

})
export type ItemDeCampanha = z.infer<typeof itemDeCampanha>

/**
 * Um responsável da campanha (PK-08 · 0138): N:M, sem principal. `desligado` deixa a tela
 * marcar quem saiu da casa — continua na campanha e é removível, mas não se acrescenta de
 * novo (a mesma régua do seletor de `/usuarios/atribuiveis`).
 */
export const responsavelDaCampanha = referenciaDeUsuario.extend({ desligado: z.boolean() })
export type ResponsavelDaCampanha = z.infer<typeof responsavelDaCampanha>

export const campanha = z.object({
  id: uuid,
  /** `PV-0246·C1` — composto pelo gatilho, a partir do pedido. */
  codigo: z.string(),
  nome: z.string(),
  pedidoId: uuid,
  pedidoCodigo: z.string(),
  clienteNome: z.string().nullable(),
  /**
   * A empresa do Cliente, para o nome abrir a ficha dela (D-136, DS-538). A campanha é a
   * fonte única do Cliente que mostra: o id sai da MESMA junção que dá o nome, e é nulo
   * quando a empresa foi excluída.
   */
  clienteEmpresaId: uuid.nullable(),
  sufixo: z.number().int(),
  inicio: dataNominal,
  fim: dataNominal,
  /** DS-06: o ÚNICO insumo de veiculação. Concluir é ato explícito (P-04); Reabrir conclusão a limpa (P-06). */
  fimReal: dataNominal.nullable(),
  /** O status no vocabulário da tela anterior (DS-657): a Pausada vem 'Em veiculação' SÓ aqui. */
  status: statusDaCampanha,
  /**
   * O status gravado (D-189, P-01), com a Pausada. A tela nova lê `statusGravado ?? status`; o campo é opcional só
   * para o dublê anterior continuar valendo: o servidor sempre o manda.
   */
  statusGravado: statusGravadoDaCampanha.optional(),
  /** Ausência deliberada, nunca zero inventado, quando a flag financeira está desligada. */
  valor: z.string().nullable(),
  moeda: codigoDeMoeda,
  /**
   * EXPAND do PK-08: o ESPELHO do primeiro responsável da última escrita, para a SPA
   * anterior. A fonte é `responsaveis`; os dois saem no contract.
   */
  responsavelId: uuid.nullable(),
  responsavelNome: z.string().nullable(),
  /** Os responsáveis, em ordem de nome. "Minhas" e `editar_proprio` = "é um dos" (PK-08). */
  responsaveis: z.array(responsavelDaCampanha),
  /**
   * Quando a campanha nasceu — a data, não o instante.
   *
   * Existia na tabela desde a 0013 e não era devolvida, o que deixava a linha do tempo da
   * 5.2 sem o evento "Campanha criada". Vai como data nominal porque é o que a tela mostra
   * (um dia), e é a mesma forma de `inicio`, `fim` e `canceladoEm`.
   */
  criadaEm: dataNominal,
  canceladoEm: dataNominal.nullable(),
  motivoCancelamentoId: uuid.nullable(),
  motivoCancelamentoNome: z.string().nullable(),
  itens: z.array(itemDeCampanha),
  /**
   * A versão da composição (D-190, P-21, DS-646): avança pelo banco a cada transação que muda os itens (G5,
   * 0195). A tela devolve no salvamento da composição a versão que leu; divergente é 409, e a tela recarrega.
   */
  versaoComposicao: z.number().int().nonnegative(),
  /**
   * Os outros custos GERAIS da campanha (0137): `null` = nenhum custo adicional declarado,
   * sem parcela automática; `"0.00"` = zero explícito. Quantia: vem `null` para quem não tem
   * a flag financeira de custos, na régua de `outrosReais`.
   */
  outrosCustosGerais: z.string().nullable().default(null),
  /** Compatibilidade com a API anterior. A versão atual devolve null, sem parâmetro implícito. */
  outrosCustosGeraisDoParametro: z.string().nullable().default(null),
  /** A campanha tem Campaign DV360 vinculada (não anulada): arquivar é recusado (G6, §5.5; DS-668). */
  temVinculoDv360: z.boolean().default(false),
  /**
   * PED-2 (D-193 (1)): as campanhas vivas do Pedido (sem as canceladas e as arquivadas) somam mais que o valor dele,
   * porque o Pedido foi reduzido depois. É o alerta "Campanhas acima do valor do Pedido" (5.1 e 5.2): a campanha não
   * aumenta o excesso (criar ou aumentar sem saldo continua recusado), e reduzir, cancelar e arquivar ficam livres.
   * `null` quando não excedem. As quantias vêm nulas sem as flags de valores; o sinal fica.
   */
  acimaDoValorDoPedido: z.object({
    valorDoPedido: z.string().nullable(),
    somaDasCampanhas: z.string().nullable(),
  }).nullable().default(null),
  /**
   * Por que quem lê não pode vincular Campaigns DV360 (P-58: o aviso no lugar do diálogo, na ficha e depois da Nova
   * campanha): a integração desligada, a entidade do Sandbox (D-192), a permissão (quem edita os custos) ou o status;
   * nulo quando pode. O catálogo é o do domínio, o mesmo do bloco DV360.
   */
  motivoSemVinculoDv360: z.enum(MOTIVOS_SEM_VINCULO_DV360).nullable().default(null),
  capacidades: z.object({
    podeEditar: z.boolean(),
    /** Cancelar (P-05, §5.6): quem edita a campanha e tem o marcador `excluir`, em Programada, Em andamento ou Pausada. */
    podeCancelar: z.boolean(),
    podeVerMargem: z.boolean(),
    podeEditarCustos: z.boolean(),
    /**
     * Reclassificar um item (P-25, DS-647): quem edita a campanha, em Programada, Em veiculação ou
     * Encerrada; nunca em Cancelada. Não exige editar custos.
     */
    podeReclassificar: z.boolean(),
    /**
     * Na reclassificação que troca o produto, adotar a meta padrão do produto novo (P-24, DS-647): quem
     * edita os custos, e nunca na campanha encerrada.
     */
    podeAdotarMetaPadrao: z.boolean(),
    /**
     * Registrar gasto (P-17, DS-653): quem edita os custos, de Programada a Concluída; nunca em Cancelada. Em item do grupo
     * DV360 é o "Usar gasto manual" (só com um item no grupo).
     */
    podeRegistrarGasto: z.boolean().default(false),
    /** Fonte do gasto, Voltar ao DV360 e a sugestão com um item (P-18, DS-653): quem edita os custos, fora da Cancelada. */
    podeDefinirFonteDoGasto: z.boolean().default(false),
    /*
      Os atos de status (§5.4, §5.6; DS-657): cada um com a permissão dele e o status de partida. Nenhum muda nada no
      DV360. O menu "Alterar status" mostra os que vierem verdadeiros.
    */
    /** Iniciar: Programada → Em andamento (quem edita a campanha). */
    podeIniciar: z.boolean().default(false),
    /** Iniciar pede o início efetivo: o planejado ainda não chegou (P-03). Falso fora da Programada. */
    iniciarPedeInicioEfetivo: z.boolean().default(false),
    /** Pausar: Em andamento → Pausada (quem edita a campanha). */
    podePausar: z.boolean().default(false),
    /** Retomar: Pausada → Em andamento (quem edita a campanha). */
    podeRetomar: z.boolean().default(false),
    /** Concluir: Em andamento ou Pausada → Concluída, com a data efetiva (quem edita a campanha; P-04). */
    podeConcluir: z.boolean().default(false),
    /** Reabrir conclusão: Concluída → Em andamento, com motivo (`midia · editar_todos`; P-06). */
    podeReabrirConclusao: z.boolean().default(false),
    /*
      A edição por status (§5.5, P-10; DS-657), já cruzada com a permissão de quem lê: a tela não decide sozinha.
    */
    /** Salvar a composição: quem edita os custos, de Programada a Pausada. */
    podeCompor: z.boolean().default(false),
    /** Alterar valor: quem edita a campanha e vê valores, de Programada a Concluída. */
    podeAlterarValor: z.boolean().default(false),
    /** Nome: quem edita a campanha, de Programada a Concluída. */
    podeEditarNome: z.boolean().default(false),
    /** Responsáveis: quem edita a campanha, de Programada a Concluída. */
    podeEditarResponsaveis: z.boolean().default(false),
    /** Datas: quem edita a campanha, de Programada a Pausada; na Concluída, só depois de Reabrir conclusão. */
    podeEditarDatas: z.boolean().default(false),
    /** Em andamento e Pausada já começaram: o início, se mudar, fica até hoje (P-09). */
    inicioAteHoje: z.boolean().default(false),
    /*
      DV360 (§5.1, §5.3, §5.5, §5.6, P-58; DS-668). A Nova campanha lê as mesmas capacidades no corpo do POST; o motivo
      de não poder vincular e o estado do vínculo vêm fora das capacidades (`motivoSemVinculoDv360`, `temVinculoDv360`).
    */
    /** Vincular Campaigns: quem edita os custos, com a integração ativa, de Programada a Concluída. */
    podeVincularDv360: z.boolean().default(false),
    /** Desvincular: quem edita os custos, em qualquer status, com a integração ligada ou não. */
    podeDesvincularDv360: z.boolean().default(false),
    /**
     * Arquivar ESTA campanha: o predicado do `DELETE /campanhas/:id` (quem edita, com o marcador `excluir`) e sem vínculo
     * DV360 não anulado (§5.5). O "Desfazer" da criação também lê daqui.
     */
    podeArquivar: z.boolean().default(false),
  }),
})
export type Campanha = z.infer<typeof campanha>

/**
 * A projeção da campanha REDUZIDA ao que cabe numa linha (spec 5.1).
 *
 * A lista mostra três números por campanha — custo planejado, custo real e margem sobre
 * mídia. São os mesmos da ficha 5.2, apurados pela mesma função; o que muda é que aqui não
 * cabe a decomposição por produto, e a lista não tem espaço para as pendências uma a uma.
 *
 * Quando a projeção não fecha (falta o custo de mídia de um produto, falta um parâmetro do
 * catálogo), os números vêm `null` e `pendencias` diz o que falta — a linha AVISA em vez de
 * mostrar zero, que seria afirmar margem nula (DS-99). Vão todas, e não a primeira: quem
 * decide quantas cabem na linha é a tela, e um "e mais duas" montado aqui seria a API
 * escolhendo layout.
 *
 * Lista vazia com números nulos é outra coisa, e não é defeito: é o nível sem permissão
 * financeira, que vê que a campanha TEM projeção sem ver quanto — e sem ver as pendências,
 * que contam o que falta no custo de um produto.
 *
 * Desde a D-188 a margem da linha é a margem PROJETADA, pelo custo de mídia planejado (o mesmo número da ficha), e o
 * gasto vem ao lado, em `gasto`, pela mesma função de acompanhamento da ficha (§4.3, §5.7). `custoApurado` e
 * `seloDaMargem` ficam por uma release com o valor neutro (`null`), para a tela anterior (P-16, DS-651).
 */
export const projecaoNaLinha = z.object({
  /** O custo de mídia PLANEJADO da campanha (Σ do planejado dos itens). */
  custoPlanejado: z.string().nullable(),
  /** Campo da D-133, mantido por uma release (P-16): vem sempre `null`. O gasto está em `gasto`. */
  custoApurado: z.string().nullable(),
  custoReal: z.string().nullable(),
  margem: z.string().nullable(),
  margemPct: z.string().nullable(),
  seloDaMargem,
  pendencias: z.array(z.string()),
  /**
   * O gasto da campanha na linha (§5.7, DS-654), lido da mesma função da ficha. `null` para quem não vê custos (DS-278,
   * P-20); sem as flags de valores, as quantias vêm nulas e o resto fica.
   */
  gasto: z.object({
    /** Σ dos M(i) existentes em V; `null` sem nenhuma apuração. */
    gastoManual: z.string().nullable(),
    /** O total, só com toda parcela conhecida; `null` com os motivos ("—" na lista, vazio no CSV, nunca zero). */
    gastoTotal: z.string().nullable(),
    /** As parcelas conhecidas, sem risco de duplicação. */
    gastoConhecido: z.string().nullable(),
    motivos: z.array(motivoDoGastoIndisponivel),
    acimaDoPlanejado: acimaDoPlanejado.nullable(),
    consolidado: consolidadoDv360NoGasto.nullable(),
    campaignsVinculadas: z.number().int().nonnegative(),
  }).nullable().default(null),
})
export type ProjecaoNaLinha = z.infer<typeof projecaoNaLinha>

/**
 * A linha da LISTA é uma forma diferente da ficha, e por isso tem tipo próprio.
 *
 * A alternativa seria pôr `projecao` e `setor` como opcionais em `campanha` e deixá-los
 * vazios na ficha — um contrato cujo significado muda conforme o endpoint que respondeu.
 * A ficha não devolve projeção resumida porque tem `GET /campanhas/:id/margem`, com a
 * decomposição inteira; e não devolve setor porque quem abre a ficha já sabe de quem ela é.
 */
export const linhaDeCampanha = campanha.extend({
  /** Derivado do tipo do Cliente da venda (D-32: `tipo_empresa.setor` é fonte única). */
  setor: z.enum(['Público', 'Privado']).nullable(),
  projecao: projecaoNaLinha,
})
export type LinhaDeCampanha = z.infer<typeof linhaDeCampanha>

/**
 * Os três KPIs do topo da 5.1.
 *
 * `aFaturar` não é soma de campanha: é o saldo da **fila de faturamento** dos pedidos das
 * campanhas do recorte, e quem sabe o que é uma nota é o `billing` (lei 1 da spec 01 §2).
 * `margemOperacionalMediaPct` é **ponderada por venda**, como diz o protótipo — a média
 * simples deixaria uma campanha de mil reais pesar igual a uma de duzentos mil.
 *
 * O campo chamava-se `margemMediaSobreMidiaPct` até a **D-111**, e o nome era do rótulo
 * antigo. Renomeado junto com a tela: enquanto os dois nomes conviveram, quem abrisse o
 * código procurando "margem sobre mídia" acharia que era outra métrica — que é exatamente
 * a confusão que a decisão veio desfazer. Um conceito, um nome, um lugar.
 */
export const kpisDeCampanhas = z.object({
  emVeiculacao: z.string().nullable(),
  emVeiculacaoQtd: z.number().int(),
  aFaturar: z.string().nullable(),
  aFaturarQtd: z.number().int(),
  margemOperacionalMediaPct: z.string().nullable(),
})
export type KpisDeCampanhas = z.infer<typeof kpisDeCampanhas>

/**
 * A margem mínima do filtro avançado viaja em RAZÃO decimal, como `margemPct`.
 *
 * `0.25` é 25%. A API fala na unidade do contrato; quem traduz o "25" que a pessoa digita é
 * a tela, que já tem `percentualParaFracao` para isso.
 */
export const margemMinimaDeCampanhas = z.string()

export const ordenacaoDeCampanhas = z.enum(['campanha', 'periodo', 'venda', 'margem'])
export type OrdenacaoDeCampanhas = z.infer<typeof ordenacaoDeCampanhas>

export const respostaDeCampanhas = z.object({
  dados: z.array(linhaDeCampanha),
  total: z.number().int(),
  kpis: kpisDeCampanhas,
  capacidades: z.object({
    podeCriar: z.boolean(),
    /**
     * Se quem está olhando pode ARQUIVAR uma campanha.
     *
     * Existe porque criar e desfazer a criação exigem coisas DIFERENTES: criar pede
     * `midia · criar`; arquivar pede `editar_proprio` mais o marcador `excluir`. Quem tem
     * uma e não a outra recebia o toast com "Desfazer" e levava um 403 ao clicar — a tela
     * oferecia um ato que o servidor nunca ia deixar acontecer.
     */
    podeArquivar: z.boolean(),
  }),
})
export type RespostaDeCampanhas = z.infer<typeof respostaDeCampanhas>

/**
 * Concluir (P-04), na rota de sempre `POST /campanhas/:id/encerrar-veiculacao`: a data efetiva em que a veiculação JÁ
 * acabou, entre o início e hoje. Vale para Em andamento e Pausada.
 */
export const entradaDeEncerramentoDeVeiculacao = z.object({ fimReal: dataNominal })
export type EntradaDeEncerramentoDeVeiculacao = z.input<typeof entradaDeEncerramentoDeVeiculacao>

/**
 * Iniciar (P-03), `POST /campanhas/:id/iniciar`: com o início planejado até hoje, sem corpo (ou `{}`); com o
 * planejado no futuro, o início efetivo, até hoje e até o fim, que substitui o planejado (de→para na trilha).
 */
export const entradaDeInicioDaCampanha = z.object({ inicioEfetivo: dataNominal.optional() })
export type EntradaDeInicioDaCampanha = z.input<typeof entradaDeInicioDaCampanha>

/** Pausar (§5.4), `POST /campanhas/:id/pausar`: o motivo é opcional, até 500 caracteres. */
export const entradaDePausaDaCampanha = z.object({ motivo: z.string().optional() })
export type EntradaDePausaDaCampanha = z.input<typeof entradaDePausaDaCampanha>

/** Retomar (§5.4), `POST /campanhas/:id/retomar`: sem campos. */
export const entradaDeRetomadaDaCampanha = z.object({})
export type EntradaDeRetomadaDaCampanha = z.input<typeof entradaDeRetomadaDaCampanha>

/** Reabrir conclusão (P-06), `POST /campanhas/:id/reabrir-conclusao`: o motivo, de 10 a 500 caracteres. */
export const entradaDeReaberturaDaConclusao = z.object({ motivo: z.string() })
export type EntradaDeReaberturaDaConclusao = z.input<typeof entradaDeReaberturaDaConclusao>

export const entradaDeCancelamentoDeCampanha = z.object({ motivoId: uuid })
export type EntradaDeCancelamentoDeCampanha = z.input<typeof entradaDeCancelamentoDeCampanha>

export const entradaDeValorDaCampanha = z.object({ novoValor: z.string() })
export type EntradaDeValorDaCampanha = z.input<typeof entradaDeValorDaCampanha>

/** Fração no fio: 0 a 1 (100%), com até seis casas, como dv_pct. Sem coerção/arredondamento. */
const percentualDaLinha = z.string({ error: 'Informe um percentual de 0 a 100%, com até quatro casas decimais.' })
  .regex(/^(?:0(?:\.\d{1,6})?|1(?:\.0{1,6})?)$/, 'Informe um percentual de 0 a 100%, com até quatro casas decimais.')

export const entradaDeItemDeCampanha = z.object({
  itemPedidoId: uuid,
  valor: z.string(),
  custoMidiaPct: percentualDaLinha.nullish(),
  /**
   * O custo de mídia em dinheiro. `dinheiroNaoNegativo`, e não `z.string()` como os
   * percentuais ao lado, porque `dv_amount` é `numeric(18,2)`: um "1234.567" que chegasse
   * ao banco seria ARREDONDADO em silêncio, e o número gravado deixaria de ser o número
   * digitado. Recusar na fronteira é a regra da casa para fração de centavo.
   */
  custoMidiaReais: dinheiroNaoNegativo.nullish(),
  licenciamentoPct: percentualDaLinha.nullish(),
  outrosReais: dinheiroNaoNegativo.nullish(),
}).superRefine((item, contexto) => {
  /*
    Percentual OU valor, nunca os dois. O CHECK da 0096 é a ÚLTIMA trava — esta é a
    primeira, e é a única que sabe o nome do campo: o 23514 do banco chega à tela como
    "esta combinação de valores não é válida", sem dizer qual linha nem qual campo.

    Aqui e não no controlador porque é a mesma definição que o front usa: a tela pode
    impedir o estado antes de enviar, e uma terceira cópia da regra no meio do caminho
    seria código que nenhuma requisição alcança.
  */
  const temPct = item.custoMidiaPct !== undefined && item.custoMidiaPct !== null
  const temValor = item.custoMidiaReais !== undefined && item.custoMidiaReais !== null
  if (temPct && temValor) {
    contexto.addIssue({
      code: 'custom',
      path: ['custoMidiaReais'],
      message: 'Informe o custo de mídia em percentual ou em valor, nunca nos dois.',
    })
  }
})
export type EntradaDeItemDeCampanha = z.input<typeof entradaDeItemDeCampanha>

/**
 * Uma linha do corpo da composição (D-193 (2), DS-686 (d)): a GRAVADA que fica vem com o `id` e só os parâmetros; a
 * NOVA vem sem id, com o item do Pedido de origem (`itemPedidoId`) e, quando a tela escolhe, a classificação
 * (`produtoId` e `subprodutoId`, os dois juntos; sem eles, a linha nasce com a classificação do item do Pedido). A linha
 * gravada nunca muda origem nem classificação (só o Reclassificar muda a classificação). Os erros apontam o caminho no
 * corpo (`linhas.N.campo`).
 */
export const linhaDaComposicaoDaCampanha = z.object({
  id: uuid.optional(),
  itemPedidoId: uuid.optional(),
  produtoId: uuid.optional(),
  subprodutoId: uuid.nullable().optional(),
  valor: dinheiroNaoNegativo,
  custoMidiaPct: percentualDaLinha.nullish(),
  custoMidiaReais: dinheiroNaoNegativo.nullish(),
  licenciamentoPct: percentualDaLinha.nullish(),
  outrosReais: dinheiroNaoNegativo.nullish(),
}).superRefine((linha, contexto) => {
  if (linha.id !== undefined) {
    for (const campo of ['itemPedidoId', 'produtoId', 'subprodutoId'] as const) {
      if (linha[campo] !== undefined) {
        contexto.addIssue({
          code: 'custom', path: [campo],
          message: 'A linha gravada não muda de origem nem de classificação: para trocar a classificação, use o Reclassificar.',
        })
      }
    }
  } else if (linha.itemPedidoId === undefined) {
    contexto.addIssue({ code: 'custom', path: ['itemPedidoId'], message: 'A linha nova diz o item do Pedido de que ela nasce.' })
  } else if ((linha.produtoId === undefined) !== (linha.subprodutoId === undefined)) {
    contexto.addIssue({
      code: 'custom', path: [linha.produtoId === undefined ? 'produtoId' : 'subprodutoId'],
      message: 'A classificação da linha nova vem inteira: o produto e o sub-produto (nulo quando não há).',
    })
  }
  // Percentual OU valor, nunca os dois: a mesma primeira trava do item da criação (o CHECK da 0096 é a última).
  if (linha.custoMidiaPct != null && linha.custoMidiaReais != null) {
    contexto.addIssue({
      code: 'custom', path: ['custoMidiaReais'], message: 'Informe o custo de mídia em percentual ou em valor, nunca nos dois.',
    })
  }
})
export type LinhaDaComposicaoDaCampanha = z.input<typeof linhaDaComposicaoDaCampanha>

/**
 * O corpo do `PUT /campanhas/:id/itens` desde a v1.9.0 (D-193 (3), DS-686 (d); §5.2 do plano): a composição inteira num
 * ato só. `linhas` são as que ficam (por id) e as novas; `removidas`, os ids das gravadas que saem, sempre explícitos:
 * os ids gravados têm de ser exatamente os de `linhas` mais os de `removidas`. A soma das linhas é o valor da campanha,
 * e a composição que tinha linhas não fica vazia (422 `campanha.composicao_sem_linhas`). A versão é a que a tela leu
 * (D-190, P-21): divergente é 409 `campanha.composicao_conflito`. O corpo da tela anterior (a lista, ou `{itens}`)
 * recebe 409 `campanha.composicao_sem_versao` ("atualize a tela"). `outrosCustosGerais` ausente não mexe, `null` retira a declaração
 * de custo opcional e `"0"` é zero explícito.
 */
export const entradaDaComposicaoDaCampanha = z.object({
  linhas: linhaDaComposicaoDaCampanha.array(),
  removidas: uuid.array(),
  outrosCustosGerais: dinheiroNaoNegativo.nullable().optional(),
  versaoComposicao: z.number().int().nonnegative(),
})
export type EntradaDaComposicaoDaCampanha = z.input<typeof entradaDaComposicaoDaCampanha>

/**
 * O ato Reclassificar de UM item da campanha (D-190, P-22 a P-25, DS-647):
 * `POST /campanhas/:id/itens/:itemId/reclassificar`, onde `itemId` é o id do item da campanha.
 *
 * Muda só a classificação OPERACIONAL; o Pedido, as outras campanhas, as apurações e a meta efetiva ficam.
 * `subprodutoId` nulo é o item sem sub-produto. `adotarMetaPadrao` é a escolha explícita de passar a herdar
 * a meta padrão do produto novo (exige editar custos; não existe na campanha encerrada). Sem ela, a troca de
 * produto de um item que herdava o padrão grava no item o percentual que valia (P-24).
 */
export const entradaDeReclassificacaoDoItem = z.object({
  produtoId: uuid,
  subprodutoId: uuid.nullable(),
  adotarMetaPadrao: z.boolean().optional(),
})
export type EntradaDeReclassificacaoDoItem = z.input<typeof entradaDeReclassificacaoDoItem>

/**
 * O catálogo do Reclassificar SOB a campanha (§5.6, P-23; DS-657): `GET /campanhas/:id/reclassificacao/catalogo`, com a
 * permissão de quem edita a campanha, e não a de ver o catálogo. Só produtos ativos vendidos com veiculação: o
 * produto entra se vende sozinho com veiculação (`vendeSemSubproduto`) ou se tem sub-produto ativo que venda; os
 * sub-produtos são só esses. `temMetaPadrao` diz se adotar a meta padrão existe; o percentual
 * (`custoMidiaPctDefault`, em fração) vem só para quem edita os custos da campanha, e nulo para os demais.
 */
export const catalogoDoReclassificar = z.object({
  produtos: z.array(z.object({
    id: uuid,
    nome: z.string(),
    vendeSemSubproduto: z.boolean(),
    temMetaPadrao: z.boolean(),
    custoMidiaPctDefault: z.string().nullable(),
    subprodutos: z.array(z.object({ id: uuid, nome: z.string() })),
  })),
})
export type CatalogoDoReclassificar = z.infer<typeof catalogoDoReclassificar>

/**
 * Os motivos do Cancelar SOB a campanha (§5.6, P-05; E5T-R1, DS-670): `GET /campanhas/:id/cancelamento/motivos`, com o
 * guarda do ato (quem edita esta campanha e tem o marcador `excluir` de Mídia), sem precisar ver os Catálogos. Só os
 * motivos ATIVOS do contexto `cancelamento_campanha`, na ordem do catálogo.
 */
export const motivosDoCancelamentoDaCampanha = z.object({
  motivos: z.array(z.object({ id: uuid, nome: z.string() })),
})
export type MotivosDoCancelamentoDaCampanha = z.infer<typeof motivosDoCancelamentoDaCampanha>

const camposDaCampanha = z.object({
  pedidoId: uuid,
  nome: z.string().trim().min(1, 'Informe o nome da campanha.'),
  inicio: dataNominal,
  fim: dataNominal,
  valor: z.string(),
  moeda: codigoDeMoeda.nullish(),
  /**
   * PK-08: o CONJUNTO de responsáveis. Na edição, substitui o conjunto inteiro; vazio é
   * campanha sem responsável. A ordem só decide o espelho (o primeiro), para a SPA anterior.
   */
  responsavelIds: z.array(uuid).optional(),
  /**
   * LEGADO (expand do PK-08): a SPA anterior manda um só. O servidor o traduz para
   * `[id]` (ou `[]`, se `null`) e registra no log; sai no contract.
   */
  responsavelId: uuid.nullish(),
  /**
   * DS493: composição opcional para compatibilidade; quando enviada, nasce inteira. A SPA
   * nova manda só `itemPedidoId` e `valor` (PK-08: a meta de custo nasce na Composição); os
   * campos de custo continuam aceitos da SPA anterior, com log, até o contract.
   */
  itens: entradaDeItemDeCampanha.safeExtend({valor:dinheiroNaoNegativo}).array().min(1).optional(),
})

/** Um jeito de dizer quem responde, e não dois: a forma nova e a legada juntas é ambíguo. */
function umaFormaDeResponsavel(
  e: { responsavelIds?: readonly string[] | undefined; responsavelId?: string | null | undefined },
  contexto: z.RefinementCtx,
): void {
  if (e.responsavelIds !== undefined && e.responsavelId !== undefined) {
    contexto.addIssue({
      code: 'custom',
      path: ['responsavelIds'],
      message: 'Envie os responsáveis em responsavelIds, sem o campo antigo responsavelId.',
    })
  }
}

export const entradaDeCampanha = camposDaCampanha.superRefine(umaFormaDeResponsavel)
export type EntradaDeCampanha = z.input<typeof entradaDeCampanha>

/**
 * Edição sem defaults (DS-142), `fimReal` nem `status`: estado muda apenas pelos
 * atos nomeados (ou pelo job de abertura), nunca pelo formulário genérico.
 */
export const entradaDeEdicaoDeCampanha = camposDaCampanha
  .omit({ pedidoId: true, valor: true, itens: true })
  .partial()
  .superRefine(umaFormaDeResponsavel)
export type EntradaDeEdicaoDeCampanha = z.input<typeof entradaDeEdicaoDeCampanha>


const custoPorProdutoDaCampanha = z.object({
  itemId: uuid,
  valor: z.string().nullable(),
  /** O custo de mídia PLANEJADO do item (D-188, P-12). Quantia: nula sem as flags. */
  custoMidia: z.string().nullable(),
  /** Campo da D-133, mantido por uma release (P-16): igual a `custoMidia`. */
  metaCustoMidia: z.string().nullable(),
  /** Campo da D-133, mantido por uma release (P-16): vem sempre `null`; o gasto é acompanhamento. */
  custoMidiaApurado: z.string().nullable(),
  origemDoCustoMidia: origemDoCustoDeMidiaDoItem,
  seloDaMargem,
  licenciamento: z.string().nullable(),
  comissao: z.string().nullable(),
  outros: z.string().nullable(),
  impostos: z.string().nullable(),
  margem: z.string().nullable(),
  margemPct: z.string(),
  realizado: z.string().nullable(),
})
export type CustoPorProdutoDaCampanha = z.infer<typeof custoPorProdutoDaCampanha>

const custoRealizadoDaCampanha = z.object({
  valor: z.string().nullable(),
  porProduto: z.array(z.object({ itemId: uuid, valor: z.string().nullable() })),
})

/**
 * A margem operacional PROJETADA da campanha (D-188): o custo de mídia é sempre o PLANEJADO de cada item, e o gasto
 * (manual ou DV360) nunca o substitui; ele é acompanhamento, na leitura `GET /campanhas/:id/realizado-operacional`.
 * Os campos da D-133 (`metaCustoMidia`, `custoMidiaApurado`, `saldoDaMetaDeMidia`, `metaDosItensSemApuracao`,
 * `origemDoCustoMidia`, `itensSemApuracao`, `ultimoApontamentoEm`, `parcial`, `seloDaMargem`) ficam por uma release
 * com valores neutros, para a aba aberta com a tela anterior (P-16, DS-651): a meta igual ao custo, a origem `'meta'`,
 * todo item na meta, sem selo e sem parcial, e o resto nulo. As quantias ficam nulas sem as flags financeiras; as
 * proporções continuam visíveis (AB-184).
 */
const margemDisponivelDaCampanha = z.object({
  disponivel: z.literal(true),
  valorDaCampanha: z.string().nullable(),
  /** O custo de mídia PLANEJADO da campanha: Σ do planejado dos itens (D-188). */
  custoMidia: z.string().nullable(),
  /** Campo da D-133, por uma release (P-16): igual a `custoMidia`. */
  metaCustoMidia: z.string().nullable(),
  /** O planejado ÷ valor da campanha, em fração (`0.4` é 40%). Proporção: visível sem as flags. */
  metaCustoMidiaPct: z.string(),
  /** Campo da D-133, por uma release (P-16): sempre `null`. */
  custoMidiaApurado: z.string().nullable(),
  /** Campo da D-133, por uma release (P-16): sempre `null`; o saldo é do acompanhamento. */
  saldoDaMetaDeMidia: z.string().nullable(),
  /** Campo da D-133, por uma release (P-16): sempre `null`. */
  metaDosItensSemApuracao: z.string().nullable(),
  origemDoCustoMidia: origemDoCustoDeMidiaDaCampanha,
  itensSemApuracao: z.number().int().nonnegative(),
  /** A maior data de apuração entre os itens; `null` sem apontamento. */
  ultimoApontamentoEm: dataNominal.nullable(),
  /** A campanha ainda não encerrou (Programada ou Em veiculação): o retrato pode mudar. */
  parcial: z.boolean(),
  /** A regra única do selo (EI-34): a pílula, o Resumo e a lista leem este campo. */
  seloDaMargem,
  licenciamento: z.string().nullable(),
  comissao: z.string().nullable(),
  outros: z.string().nullable(),
  /** A parte de `outros` que é custo GERAL da campanha (PK-07): a cascata diz "inclui R$ X". */
  outrosGerais: z.string().nullable().default(null),
  /** Compatibilidade com a API anterior. A versão atual devolve false. */
  outrosGeraisDoParametro: z.boolean().default(false),
  impostos: z.string().nullable(),
  /**
   * D-151: a alíquota da NOTA (ISS + PIS + COFINS da entidade vigentes no mês de início da
   * campanha, as mesmas das Deduções da DRE), em fração (`0.0615` é 6,15%). Proporção: visível
   * sem as flags financeiras, como `metaCustoMidiaPct`. `null` só em resposta anterior a ela.
   */
  impostosDaNotaPct: z.string().nullable().default(null),
  /** D-151: o IR e CSLL estimados (`margem.ir_csll_estimado_pct`), em fração. Somado à nota. */
  irCsllEstimadoPct: z.string().nullable().default(null),
  margem: z.string().nullable(),
  margemPct: z.string(),
  metaMinimaPct: z.string(),
  porProduto: z.array(custoPorProdutoDaCampanha),
  realizado: custoRealizadoDaCampanha,
})

const margemIndisponivelDaCampanha = z.object({
  disponivel: z.literal(false),
  pendencias: z.array(z.string()).min(1),
  realizado: custoRealizadoDaCampanha,
})

export const margemDaCampanha = z.discriminatedUnion('disponivel', [
  margemDisponivelDaCampanha,
  margemIndisponivelDaCampanha,
])
export type MargemDaCampanha = z.infer<typeof margemDaCampanha>

/** Job diário e idempotente: Programada → Em veiculação quando `inicio ≤ hoje`. */
export const FILA_ABRIR_CAMPANHAS_EM_VEICULACAO = 'campanha.abrir-veiculacao'

/**
 * Registrar gasto (P-17, DS-653): o TOTAL ACUMULADO gasto no item até `apuradoEm` (a tela escreve "Total acumulado gasto
 * até a data"), sempre uma linha nova (só acréscimo). É acompanhamento, ao lado da margem projetada: não entra em
 * margem (D-188), DRE, caixa, CAP, orçamento, 8.6 nem comissão.
 *
 * - `confirmaValorMenor` e `confirmaDataAnterior`: o valor menor que o último registro do item e a data anterior a ele
 *   só passam confirmados; sem a confirmação, 409 `campanha.gasto_exige_confirmacao`, com o campo que falta.
 * - `usarGastoManual`: o ato "Usar gasto manual" do item que é o único do grupo coberto pelo consolidado DV360 (F-6): a
 *   fonte do grupo passa a manual, com o motivo, na mesma transação do registro. Sem ele, 409
 *   `campanha.item_com_fonte_dv360`; com dois ou mais itens no grupo, sempre 409
 *   `campanha.substituicao_parcial_do_consolidado` (o grupo troca inteiro, pela Fonte do gasto).
 */
export const entradaDeRealizadoOperacional = z.object({
  /**
   * A LINHA da campanha em que o gasto é registrado (D-193 (2)): é por ela que o registro é gravado e lido. Um dos dois
   * campos vem; com os dois, vale a linha, e o item tem de ser a origem dela.
   */
  itemCampanhaId: uuid.optional(),
  /**
   * A forma da tela anterior: o item do pedido, aceito quando ele tem UMA linha nesta campanha (com mais de uma,
   * `campanha.item_com_varias_linhas`).
   */
  itemPedidoId: uuid.optional(),
  valorAcumulado: dinheiroNaoNegativo,
  apuradoEm: dataNominal,
  confirmaValorMenor: z.boolean().optional(),
  confirmaDataAnterior: z.boolean().optional(),
  usarGastoManual: z.object({ motivo: z.string() }).optional(),
})
export type EntradaDeRealizadoOperacional = z.input<typeof entradaDeRealizadoOperacional>

export const apontamentoOperacional = z.object({
  id: uuid,
  /** Quantia: `null` para quem não tem a flag financeira de custos, na régua de `outrosReais`. */
  valorAcumulado: z.string().nullable(),
  apuradoEm: dataNominal,
  /** Desde a 0196, sempre `'manual'` (D-191): o consolidado DV360 nunca é gravado como apurado. */
  origem: z.enum(['manual', 'dv360']),
  registradoPor: z.string().nullable(),
  registradoEm: z.string(),
  /**
   * O estado do registro na linha (§4.4 do plano da v1.9.0), calculado no servidor pela ordem do histórico (data de
   * apuração, depois o registro, do mais novo ao mais antigo): `ultimo` é o último registro da linha (o `atual` da linha
   * viva; o que `gastoEmLinhasRemovidas` soma na removida), e `substituido`, cada um dos anteriores. Diz a posição, não
   * se o registro vale como gasto: no grupo coberto pelo consolidado DV360, o último manual não vale (`manualVigente`).
   * Opcional só para o dublê anterior: o servidor sempre o manda.
   */
  estado: z.enum(['ultimo', 'substituido']).optional(),
})
export type ApontamentoOperacional = z.infer<typeof apontamentoOperacional>

/** Uma declaração de escopo e fonte de um item (só acréscimo), como o histórico a mostra. */
export const declaracaoDaFonteDoGasto = z.object({
  escopo: escopoDoGasto,
  fonte: fonteDoGasto,
  /** `declarar` (Fonte do gasto), `usar_gasto_manual` (F-6) ou `adotar_fonte_do_grupo` (o item devolvido, P-26). */
  ato: z.enum(['declarar', 'usar_gasto_manual', 'adotar_fonte_do_grupo']),
  motivo: z.string().nullable(),
  registradoPor: z.string().nullable(),
  registradoEm: z.string(),
})
export type DeclaracaoDaFonteDoGasto = z.infer<typeof declaracaoDaFonteDoGasto>

/** O que a linha do item mostra como gasto (§4.3). As quantias vêm nulas sem as flags. */
export const gastoDaLinhaDoAcompanhamento = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('manual'), valor: z.string().nullable(), apuradoEm: dataNominal }),
  z.object({ tipo: z.literal('sem_apuracao') }),
  z.object({ tipo: z.literal('consolidado'), valor: z.string().nullable(), completo: z.boolean(), ate: dataNominal.nullable() }),
  z.object({ tipo: z.literal('nao_distribuido') }),
  z.object({ tipo: z.literal('sem_vinculo') }),
])
export type GastoDaLinhaDoAcompanhamento = z.infer<typeof gastoDaLinhaDoAcompanhamento>

/**
 * O acompanhamento do gasto da campanha (§4.3, §4.4, DS-652): a mesma função (`acompanharMidia`) serve a ficha, a lista
 * e o CSV. Nunca soma origens; a ausência nunca vira zero; o total só existe com toda parcela conhecida. É leitor de
 * custo (P-20): sem `midia_custos_margem · ver` a leitura não existe, e sem as flags de valores as quantias vêm nulas.
 */
export const acompanhamentoDaCampanha = z.object({
  /** Seleções explícitas do Modelo D, cada total automático apresentado uma vez. */
  unidadesDv360: z.array(z.object({
    acimaDoPlanejado: acimaDoPlanejado.nullable().optional(),
    chave: z.string(), linhas: z.array(uuid), planejado: z.string().nullable(),
    consolidado: z.string().nullable(), completo: z.boolean(), saldo: z.string().nullable(), ate: dataNominal.nullable(), desde: dataNominal.nullable(), descricao: z.string().nullable(),
  })).optional(),
  /** F(G); `null` sem item coberto pelas Campaigns. */
  fonteDoGrupo: fonteDoGasto.nullable(),
  fonteDoGrupoDesde: dataNominal.nullable(),
  linhas: z.array(z.object({
    /** A linha da campanha (D-193 (2)). Opcional só para o dublê anterior: o servidor sempre a manda. */
    acimaDoPlanejado: acimaDoPlanejado.nullable().optional(),
    itemCampanhaId: uuid.optional(),
    /** O item do pedido da linha, para a tela anterior (sai na v1.9.1). */
    itemPedidoId: uuid,
    escopo: escopoDoGasto.nullable(),
    /** A fonte EFETIVA da linha: dv360 só no item do grupo coberto com fonte DV360. */
    fonte: fonteDoGasto,
    /** O planejado; `null` é "Não planejado" (ou quantia oculta, sem as flags). */
    planejado: z.string().nullable(),
    naoPlanejado: z.boolean(),
    gasto: gastoDaLinhaDoAcompanhamento,
    saldo: z.string().nullable(),
    /** F-1: o manual deste item não vale desde esta data (o grupo está no consolidado). */
    manualNaoVigenteDesde: dataNominal.nullable(),
    /** P-E4-1 (leitura A): sem escopo declarado com o grupo no consolidado; fora da soma, do saldo e do "Acima". */
    foraDaSoma: z.boolean(),
  })),
  /** O grupo coberto pelo consolidado com duas ou mais linhas: "não distribuído por produto". */
  grupo: z.object({
    acimaDoPlanejado: acimaDoPlanejado.nullable().optional(),
    /** O item do pedido de cada linha do grupo, para a tela anterior (sai na v1.9.1). */
    itens: z.array(uuid),
    /** As linhas do grupo (D-193 (2)). */
    linhas: z.array(uuid).optional(),
    planejado: z.string().nullable(),
    consolidado: z.string().nullable(),
    completo: z.boolean(),
    saldo: z.string().nullable(),
  }).nullable(),
  gastoTotal: z.string().nullable(),
  gastoConhecido: z.string().nullable(),
  gastoManual: z.string().nullable(),
  /** As parcelas do total com a data de cada uma (F-7): "manual até 07/10 + Media Cost até 10/10". */
  parcelas: z.array(z.object({ origem: z.enum(['manual', 'dv360']), ate: dataNominal })),
  motivos: z.array(motivoDoGastoIndisponivel),
  /** Sem escopo declarado com o grupo no consolidado: "não somado: escopo não declarado". O item do pedido de cada linha. */
  naoSomados: z.array(uuid),
  /** As mesmas, pelas linhas (D-193 (2)). */
  naoSomadosLinhas: z.array(uuid).optional(),
  /** A soma dos saldos conhecidos; `null` sem nenhum ("Sem apuração" ou "Indisponível", P-15). */
  saldo: z.string().nullable(),
  /**
   * O planejado ainda sem apuração, à parte do saldo; "0.00" quando nada espera apuração. `null` sem as flags de valores
   * ou quando alguma parcela sem apuração é Não planejada (P-14: indisponível, nunca zero nem a soma só das planejadas).
   */
  planejadoSemApuracao: z.string().nullable(),
  /** Com gasto conhecido e sem planejamento: o saldo deles é indisponível (P-13). O item do pedido de cada linha. */
  naoPlanejados: z.array(uuid),
  /** As mesmas, pelas linhas (D-193 (2)). */
  naoPlanejadosLinhas: z.array(uuid).optional(),
  acimaDoPlanejado: acimaDoPlanejado.nullable(),
  consolidado: consolidadoDv360NoGasto.nullable(),
  campaignsVinculadas: z.number().int().nonnegative(),
})
export type AcompanhamentoDaCampanha = z.infer<typeof acompanhamentoDaCampanha>

/**
 * A origem comercial de uma linha (D-193 (1) e (4)): o item do Pedido de que ela nasceu, se ele ainda está no Pedido
 * (`presente`) ou foi excluído dele (`removida`, P2-c, desde a v1.9.1), a descrição "Produto · Subproduto" e, quando
 * removida, quando e por quem. O que não foi registrado vem nulo e a tela o diz como ausência, nunca o preenche.
 */
export const origemDaLinha = z.object({
  estado: z.enum(['presente', 'removida']),
  itemPedidoOrigemId: uuid,
  descricao: z.string().nullable(),
  removidaEm: z.string().nullable(),
  removidaPor: z.string().nullable(),
})
export type OrigemDaLinha = z.infer<typeof origemDaLinha>

/**
 * Um Reclassificar da linha (§4.5 do plano da v1.9.0; DS-647), lido da trilha pela linha: a classificação antes e
 * depois pelo NOME gravado no ato ("Produto · Subproduto", ou só "Produto"), quando e por quem (`null` = não
 * registrado). `de` ou `para` nulo: o ato só mudou o sub-produto, e daquele lado a linha ficou sem ele (a trilha não
 * repete o produto, que não mudou). Só nomes, nunca custo nem valor: vem também sem as flags de valores.
 */
export const reclassificacaoDaLinha = z.object({
  de: z.string().nullable(),
  para: z.string().nullable(),
  em: z.string(),
  por: z.string().nullable(),
})
export type ReclassificacaoDaLinha = z.infer<typeof reclassificacaoDaLinha>

/**
 * Uma linha REMOVIDA da composição (D-193 (3) e (4), §4.4 do plano da v1.9.0), pelo retrato que o banco grava na
 * remoção (`item_campanha_removida`, 0203): a classificação e o nome do momento, os parâmetros (nulos sem as flags de
 * valores), a origem, quando foi criada e removida e por quem (`null` = não registrado), e os registros de gasto e de
 * fonte dela, casados pelo id da linha. Só para consulta: nada daqui entra no Gasto total, no saldo nem na margem.
 */
export const linhaRemovidaDaCampanha = z.object({
  itemCampanhaId: uuid,
  classificacao: z.object({ produtoId: uuid, subprodutoId: uuid.nullable(), nome: z.string() }),
  parametros: z.object({
    valor: z.string().nullable(),
    custoMidiaPct: z.string().nullable(),
    custoMidiaReais: z.string().nullable(),
    licenciamentoPct: z.string().nullable(),
    outrosReais: z.string().nullable(),
  }).nullable(),
  origem: origemDaLinha,
  criadaEm: z.string(),
  removidaEm: z.string(),
  removidaPor: z.string().nullable(),
  /**
   * Os registros de gasto da linha, na ordem do último registro: data de apuração, depois o registro, do mais novo ao
   * mais antigo (a mesma régua do acompanhamento). O "estado" de cada registro (§4.4 do plano) vem calculado em
   * `estado`: o primeiro é o último registro da linha (o que `gastoEmLinhasRemovidas` soma), e os demais foram
   * substituídos por ele. Vale o mesmo para o `historico` da linha viva.
   */
  historico: z.array(apontamentoOperacional),
  /** As declarações de escopo e fonte da linha, da mais nova à mais antiga. */
  historicoDaFonte: z.array(declaracaoDaFonteDoGasto),
  /** As reclassificações da linha enquanto viva, da mais recente à mais antiga (aditivo: vazio na leitura anterior). */
  reclassificacoes: z.array(reclassificacaoDaLinha).default([]),
})
export type LinhaRemovidaDaCampanha = z.infer<typeof linhaRemovidaDaCampanha>

/**
 * A margem operacional pelo custo de mídia APURADO (D-193 (5), DS-686 (g); CAMP-008): uma simulação da ficha, só de
 * leitura, servida na leitura do gasto e em nenhum outro lugar. A margem oficial (`margemDaCampanha`, a lista, os KPIs, o
 * filtro, a ordenação e o CSV) continua só a projetada e não tem este campo.
 *
 * `disponivel: false` traz os motivos (cada motivo do acompanhamento basta, e a margem projetada indisponível também) e
 * as quantias nulas: a ausência nunca vira zero, e o planejado nunca entra no lugar do gasto que falta. As datas são a
 * menor por origem (`referencias`) e a menor de todas (`dataDeReferencia`). Sem as flags de valores, as quantias e os
 * percentuais vêm nulos, e o resto continua.
 */
export const margemEmApuracaoDaCampanha = z.object({
  disponivel: z.boolean(),
  motivos: z.array(z.object({
    codigo: z.enum([
      'margem_projetada_indisponivel', 'sem_itens', 'itens_sem_escopo', 'sem_apuracao', 'grupo_sem_vinculo',
      'consolidado_parcial', 'moeda_divergente',
    ]),
    texto: z.string(),
    /** As linhas da campanha que o motivo nomeia (`item_campanha.id`). */
    linhas: z.array(uuid),
  })),
  referencias: z.array(z.object({ origem: z.enum(['manual', 'dv360']), ate: dataNominal })),
  dataDeReferencia: dataNominal.nullable(),
  /** O custo de mídia apurado: o gasto total das linhas vivas. */
  custoMidia: z.string().nullable(),
  /** O custo de mídia projetado (o da margem oficial), como referência. */
  custoMidiaProjetado: z.string().nullable(),
  margem: z.string().nullable(),
  margemPct: z.string().nullable(),
  /** Por unidade de apuração: a linha manual, a linha única do grupo DV360 ou o grupo DV360 (não distribuído). */
  unidades: z.array(z.object({
    tipo: z.enum(['linha_manual', 'linha_dv360', 'grupo_dv360']),
    linhas: z.array(uuid),
    gasto: z.string().nullable(),
    ate: dataNominal.nullable(),
    margem: z.string().nullable(),
    margemPct: z.string().nullable(),
  })),
})
export type MargemEmApuracaoDaCampanha = z.infer<typeof margemEmApuracaoDaCampanha>

/**
 * As três leituras de custo da campanha, SEPARADAS e nomeadas (D-128): o planejado está na margem
 * (`GET /campanhas/:id/margem`, `custoMidia`); aqui estão o gasto (o histórico manual de cada linha, a fonte e o
 * acompanhamento) e o realizado EM CAP das contas de custo de mídia, e a diferença entre o manual e o CAP. Nenhuma soma
 * as outras.
 *
 * Desde a v1.9.0 (D-193 (2) a (4)) tudo casa pela LINHA da campanha: `itens` é uma entrada por linha viva, com o id e
 * a origem dela; `linhasRemovidas` traz as linhas que saíram da composição, com o histórico delas; e
 * `gastoEmLinhasRemovidas` é só informativo, nunca somado a total, saldo ou margem. Os campos novos são aditivos.
 */
export const realizadoOperacionalDaCampanha = z.object({
  /** Negociação com SPA/backend N-1: a interface contextual só chama as novas rotas quando anunciadas. */
  origemPorLinhaDisponivel: z.boolean().optional(),
  itens: z.array(z.object({
    /** A linha da campanha (D-193 (2)). Opcional só para o dublê anterior: o servidor sempre a manda. */
    itemCampanhaId: uuid.optional(),
    /** O item do pedido da linha, para a tela anterior (sai na v1.9.1). */
    itemPedidoId: uuid,
    /** A origem comercial da linha. */
    origem: origemDaLinha.optional(),
    /** O último registro do item: o de apuração mais recente. `null` = nenhum apontamento. */
    atual: apontamentoOperacional.nullable(),
    /** Todos, do mais novo ao mais antigo — o registro é append-only. */
    historico: z.array(apontamentoOperacional),
    /** O escopo declarado do item; `null` = não declarado (manual implícito). */
    escopo: escopoDoGasto.nullable().default(null),
    /** A fonte gravada do item; sem declaração, `'manual'`. */
    fonte: fonteDoGasto.default('manual'),
    /** O último registro vale como gasto? Não, quando o item está no grupo coberto pelo consolidado (F-1). */
    manualVigente: z.boolean().default(true),
    /** As declarações de escopo e fonte do item, da mais nova à mais antiga. */
    historicoDaFonte: z.array(declaracaoDaFonteDoGasto).default([]),
    /** As reclassificações da linha, da mais recente à mais antiga (aditivo: vazio na leitura anterior). */
    reclassificacoes: z.array(reclassificacaoDaLinha).default([]),
  })),
  /** Σ dos registros vigentes dos itens que entram pela via manual (V, §4.3); `null` sem nenhum (não é zero). */
  totalOperacional: z.string().nullable(),
  /** Rateio de CAP para a campanha SÓ nas contas de custo de mídia (grupo de DRE repasse). */
  capMidia: z.string().nullable(),
  /** Manual − CAP de mídia; `null` quando falta um dos dois. Sem "vencedor". */
  diferenca: z.string().nullable(),
  /** O acompanhamento do gasto (§4.3). */
  acompanhamento: acompanhamentoDaCampanha.nullable().default(null),
  /** A margem pelo custo de mídia apurado, só de leitura (D-193 (5)); `null` sem a leitura (a API anterior). */
  margemEmApuracao: margemEmApuracaoDaCampanha.nullable().default(null),
  /** Somente ficha interna: acompanhamento em atualização, nunca margem oficial ou Portal. */
  provisorio: z.object({
    ativo: z.boolean(), situacao: z.enum(['disponivel','atrasado','indisponivel']),
    parcela: z.string().nullable(), atualizadoEm: z.string().nullable(), geradoEm: z.string().nullable(),
    dias: z.array(z.string()), fusos: z.array(z.string()), dadosCompletosAte: z.string().nullable(),
    linhasComProvisorio: z.array(uuid),
    acompanhamento: acompanhamentoDaCampanha.nullable(),
    /** Compatibilidade temporária com SPA anterior; nova API devolve null e a nova SPA aceita ausência. */
    margem: margemEmApuracaoDaCampanha.nullable().optional(),
  }).nullable().optional(),
  /** As linhas removidas da composição, da remoção mais recente à mais antiga, com o histórico de cada uma. */
  linhasRemovidas: z.array(linhaRemovidaDaCampanha).default([]),
  /**
   * A soma do último registro de gasto de cada linha removida. INFORMATIVO: "Gasto em linhas removidas: R$ X (fora do
   * Gasto total)"; nunca entra em total, saldo nem margem. `null` sem as flags de valores ou sem registro.
   */
  gastoEmLinhasRemovidas: z.string().nullable().default(null),
  /**
   * Os itens do PEDIDO fora da composição, na ordem do pedido (V-04; DS-669 (b)): por item, se ele já esteve nesta
   * campanha. Sem quantias. Desde a v1.9.0 nada volta com o item (P3-B, D-193 (3)): o item incluído de novo é uma linha
   * nova, que começa limpa, e os registros da que saiu ficam em `linhasRemovidas`. As duas flags que a v1.9.0 mandava
   * sempre `false` para a tela anterior (apuração e declaração do par) saíram (C-02): o objeto não as lê, e a resposta
   * de uma API anterior que ainda as traga passa, sem elas.
   */
  itensForaDaComposicao: z.array(z.object({
    itemPedidoId: uuid,
    /**
     * Já esteve nesta campanha: a trilha da composição o incluiu ou removeu (DS-646 (d)), há o retrato de uma linha que
     * nasceu dele (0203), ou há registro de gasto ou de fonte feito numa linha dele (a composição da v1.7.0 não nomeava o
     * item na trilha).
     */
    jaEsteveNaCampanha: z.boolean(),
  })).default([]),
})
export type RealizadoOperacionalDaCampanha = z.infer<typeof realizadoOperacionalDaCampanha>

/**
 * Fonte do gasto, o ato da campanha inteira (F-5, P-18, DS-653): `PUT /campanhas/:id/fonte-do-gasto`. Serve também à
 * sugestão com um item (F-4: "Usar o consolidado DV360" ou "Manter manual") e ao "Voltar ao DV360" (F-13).
 *
 * - `itens`: o escopo de cada item que a pessoa marcou; o item que não vem fica como está. Sem pré-seleção.
 * - `fonteDoGrupo`: a fonte única do grupo coberto, obrigatória quando algum item fica coberto e `null` sem nenhum. A
 *   fonte dv360 exige Campaign vinculada e a mesma moeda.
 * - `motivo`: de 10 a 500 caracteres, obrigatório para corrigir um escopo já declarado e para trocar a fonte do grupo.
 * - `gastoManual`: o valor que a pessoa digitou no registro recusado pela troca parcial (F-6, caminho a), gravado só
 *   aqui, na mesma transação, se o item passar mesmo à apuração manual. Forma da tela anterior.
 * - `gastosManuais`: os registros novos do mesmo ato, um por LINHA (D-193 (6), §6.3 do plano da v1.9.0): o diálogo "De
 *   onde vem o gasto de mídia" grava o escopo, a fonte e o valor informado de cada linha que passa ao manual numa
 *   transação só, depois das declarações; cada um passa pela mesma régua do Registrar gasto (recusa no campo
 *   `gastosManuais.<i>.…`), e a mesma linha duas vezes é 422 `campanha.item_repetido`. Aditivo: a tela anterior não o
 *   manda, e o corpo dela continua valendo.
 */
export const entradaDaFonteDoGasto = z.object({
  /**
   * O escopo de cada LINHA (`itemCampanhaId`, D-193 (2)); a tela anterior manda o item do pedido (`itemPedidoId`), aceito
   * quando ele tem uma linha só nesta campanha.
   */
  itens: z.array(z.object({ itemCampanhaId: uuid.optional(), itemPedidoId: uuid.optional(), escopo: escopoDoGasto })),
  fonteDoGrupo: fonteDoGasto.nullable(),
  motivo: z.string().nullish(),
  gastoManual: entradaDeRealizadoOperacional.omit({ usarGastoManual: true }).optional(),
  gastosManuais: z.array(entradaDeRealizadoOperacional.omit({ usarGastoManual: true })).optional(),
})
export type EntradaDaFonteDoGasto = z.input<typeof entradaDaFonteDoGasto>
